import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import backendAPI from '../api/backendApi'; // ✅ Correct import

const LoginPage = () => {
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await backendAPI.loginUser(email, password); // ✅ Use class method
      
      if (response.success) {
        // Store auth token
        localStorage.setItem('sportiq_token', response.token);
        
        // Update user context
        setUser({
          email: response.user.email,
          username: response.user.username || `${response.user.firstName} ${response.user.lastName}`,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          isAdmin: ['admin@sportiq.bet', 'artur@sportiq.bet', 'rocky@sportiq.bet'].includes(response.user.email)
        });

        // Redirect to games page
        navigate('/games');
      } else {
        setError(response.error);
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // More specific error messages
      let errorMessage = 'Login failed. Please try again.';
      
      if (err.message.includes('fetch')) {
        errorMessage = 'Connection failed. Please check your internet connection and try again.';
      } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (err.message.includes('404')) {
        errorMessage = 'Account not found. Please check your email or sign up for a new account.';
      } else if (err.message.includes('500')) {
        errorMessage = 'Server error. Please try again in a few moments.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sportiq-dark flex items-center justify-center">
      <div className="max-w-md w-full space-y-8 p-8">
        {/* Logo/Header */}
        <div className="text-center">
          <div className="flex justify-center items-center mb-4">
            <img 
              src="/sportiq-logo.png" 
              alt="SportIQ Logo" 
              className="h-12 w-12 mr-3"
            />
            <h1 className="text-2xl font-bold text-white">
              Sport<span className="text-sportiq-orange">IQ.bet</span>
            </h1>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
          <p className="text-sportiq-dark-gray">Sign in to your SportIQ account</p>
        </div>

        {/* Login Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-sportiq-gray text-white rounded-lg border border-gray-600 focus:border-sportiq-orange focus:outline-none transition-colors"
                placeholder="Enter your email"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 bg-sportiq-gray text-white rounded-lg border border-gray-600 focus:border-sportiq-orange focus:outline-none transition-colors"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.05 6.05M9.878 9.878a3 3 0 105.121-.045m-5.121.045l-.176-.176m5.121.045l.176-.176m0 0L15.95 6.05m-4.242 4.242L15.95 6.05" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-600 bg-opacity-20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-bold text-lg transition-colors ${
              loading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-sportiq-orange text-white hover:bg-orange-600'
            }`}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>

          {/* Sign Up Link */}
          <div className="text-center">
            <p className="text-sportiq-dark-gray">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-sportiq-orange hover:text-orange-400 font-semibold"
              >
                Sign up here
              </Link>
            </p>
          </div>
        </form>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <div className="bg-sportiq-gray rounded-lg p-4">
            <h3 className="text-white font-semibold mb-2">Getting Started</h3>
            <p className="text-sm text-sportiq-dark-gray">
              Make your NFL picks • $25 per scorecard • Up to 3 scorecards per week
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;